import { useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { keys } from "lodash";
import {
  useAttachmentDataInfinite,
  useDownloadAttachment
} from "@/entities/attachments/hooks";
import {
  buildDreamerTableColumns,
  buildDreamerTableFilterOptions
} from "@/components/global/Chat/components/dreamerCharts/table/dreamerTableColumns";
import { useDreamerAttachmentToolbarState } from "@/hooks/useDreamerAttachmentToolbarState";
import { hasActiveFilterValue } from "@/components/controls/ToolbarActions/utils";
import { EmptyFilters } from "@/services/api/types";
import { DreamerAttachmentFilterMetadata } from "@/entities/attachments/types";

export const useDreamerAttachmentData = (
  metadata: DreamerAttachmentFilterMetadata,
  // The name to save the downloaded file under, minus its extension, when the
  // caller knows one the user can change. Absent, the file keeps the name the
  // attachment was stored with.
  { downloadFilenameStem }: { downloadFilenameStem?: string | null } = {}
) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { filters, setFilters, searchTerm, setSearchTerm, searchQuery } =
    useDreamerAttachmentToolbarState();

  const sortField = sorting[0]?.id;
  const sortAsc = sorting[0] ? !sorting[0].desc : undefined;

  const attachmentId = metadata.attachmentId ?? "";

  const {
    rows,
    totalRows,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error
  } = useAttachmentDataInfinite(attachmentId, {
    filters,
    search: searchQuery,
    sortField,
    sortAsc,
    enabled: !!attachmentId
  });

  const { download, isDownloading } = useDownloadAttachment(attachmentId, {
    filenameStem: downloadFilenameStem
  });

  const previewColumns = useMemo(
    () => buildDreamerTableColumns(metadata.columns),
    [metadata.columns]
  );

  const sortableColumns = useMemo(
    () => buildDreamerTableColumns(metadata.columns, { enableSorting: true }),
    [metadata.columns]
  );

  const filterOptions = useMemo(
    () => buildDreamerTableFilterOptions(metadata.columns),
    [metadata.columns]
  );

  const hasActiveFilters = keys(filters).some((key) =>
    hasActiveFilterValue(filters[key])
  );
  const hasSearchTerm = !!searchTerm;

  const clearFilters = () => setFilters(EmptyFilters);
  const clearSearch = () => setSearchTerm("");

  return {
    attachmentId,
    rows,
    totalRows,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    download,
    isDownloading,
    sorting,
    setSorting,
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,
    hasActiveFilters,
    hasSearchTerm,
    clearFilters,
    clearSearch,
    previewColumns,
    sortableColumns,
    filterOptions
  };
};
